import { Play, Square, Wand2, FileSearch, ChevronDown, FileText, FolderOpen, Save, SaveAll, Settings2, WrapText, Check, LayoutGrid, AlignJustify, BotMessageSquare, Bug, Search, MousePointerClick, TextSelect } from 'lucide-react';
import { Button } from '../../../../components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '../../../../components/ui/popover';
import { memo, useEffect, useMemo, useRef, useState } from 'react';
import { useAppStore, type EditorSettings } from '../../../../store/appStore';
import type { QueryViewMode, TabEditorOptions, TransactionMode, TransactionState, TransactionIsolationLevel } from '../../core/types/workspaceTypes';
import type { IsolationLevelDropdownState } from '../hooks/useTransactionControl';
import { RecentQueriesDropdown } from '../../../query-history';
import { useCommandPaletteStore } from '../../../command-palette/store/commandPaletteStore';
import { ResponsiveToolbar, ToolbarSection, useResponsiveToolbar } from '../../../../components/ui/responsive-toolbar';
import { TransactionToolbarControls } from './TransactionToolbarControls';
import { WorkspacePinButton } from '../../common/components/WorkspacePinButton';
import { isExecutableStatement } from '../../common/utils/peekResultsDetection';
import { OptimizeQueryButton } from './OptimizeQueryButton';
import { QueryViewToggle } from '../visual-mode/QueryViewToggle';
import { useShortcutLabel } from '../../../keybindings/hooks/useShortcutLabel';

import { RandomDotmSpinner } from '@/components/ui/RandomDotmSpinner';

interface QueryToolbarProps {
  /** Owning query tab id — drives the pin/unpin affordance (Task 20). */
  tabId: string;
  onExecute: () => void;
  /**
   * Run only the current Monaco selection. Wired in from `QueryEditorTab` and
   * called by the toolbar Execute button when it's in "Selection" mode (i.e.
   * the inline Execute bar setting is OFF and the selection parses as a
   * complete, runnable statement that isn't the whole editor).
   */
  onExecuteSelection?: (selectedSql: string) => void;
  /**
   * The current Monaco selection text — used to drive "Selection" mode. Empty
   * string when there's no selection. The toolbar uses this both for the
   * runnability check and as the SQL passed to `onExecuteSelection`.
   */
  selectedText?: string;
  onExecuteWithActualPlan?: () => void;
  /**
   * `'statement'` while the Visual view shows: Execute runs the shown
   * statement, and says so ("Statement"); selection mode is off.
   */
  executeScope?: 'statement';
  /** SQL | Visual view of this tab (the toggle renders only with the VQE beta on). */
  viewMode?: QueryViewMode;
  onViewModeChange?: (mode: QueryViewMode) => void;
  onStop?: () => void;
  onFormat?: () => void;
  onExplainPlan?: () => void;
  onOpenFile?: () => void;
  onSaveFile?: () => void;
  onSaveFileAs?: () => void;
  isExecuting?: boolean;
  isCancelling?: boolean;
  isExplainingPlan?: boolean;
  isOpeningFile?: boolean;
  isSavingFile?: boolean;
  disabled?: boolean;
  /** Whether the connection is disconnected */
  isDisconnected?: boolean;
  /** Current tab's editor options */
  editorOptions?: TabEditorOptions;
  /** Callback when editor options change (updates both tab and global defaults) */
  onEditorOptionsChange?: (options: Partial<TabEditorOptions>) => void;
  /** Connection ID for recent queries */
  connectionId?: string;
  /** Database context for recent queries */
  database?: string;
  /** Callback when a recent query is selected */
  onSelectRecentQuery?: (sql: string) => void;
  // Transaction controls
  transactionMode?: TransactionMode;
  transactionState?: TransactionState;
  transactionIsolationLevel?: TransactionIsolationLevel;
  onTransactionModeChange?: (mode: TransactionMode) => void;
  onTransactionIsolationLevelChange?: (level: TransactionIsolationLevel) => void;
  onTransactionCommit?: () => void;
  onTransactionRollback?: () => void;
  transactionCommitEnabled?: boolean;
  transactionRollbackEnabled?: boolean;
  transactionModeChangeDisabled?: boolean;
  transactionIsolationLevelState?: IsolationLevelDropdownState;
  /** Whether the AI chat sidebar is open */
  isAiChatOpen?: boolean;
  /** Toggle the AI chat sidebar */
  onToggleAiChat?: () => void;
  /**
   * Whether the editor content can be started as an ANONYMOUS PL/SQL block
   * (for the Debug button). Not "is this PL/SQL" — `CREATE …` routine DDL is
   * PL/SQL but cannot be debugged as a block; see
   * `src/core/engines/oraclePlsqlDetection.ts`.
   */
  isAnonymousPlsqlBlock?: boolean;
  /** Debug callback — opens debugger tab for the current anonymous PL/SQL block */
  onDebug?: () => void;
  /**
   * T13 — "AI: Optimize this query" (right section, between Format and
   * Options — moved off the Options dropdown in R2). Undefined (not just
   * disabled) when the tab has no connection yet, mirroring how
   * `onSaveFileAs` is conditionally passed — the button is hidden rather than
   * shown disabled for a reason unrelated to the AI CLI gate below.
   */
  onOptimizeQuery?: () => void;
  /** Same `isAiLauncherDisabled` gate used everywhere else in this feature. */
  aiCliAvailable?: boolean;
  aiCliDetectionLoading?: boolean;
}

// Memoised: QueryEditorTab re-renders on execution progress and settled
// selection changes, and this toolbar (dropdowns, transaction controls,
// responsive-overflow measurement) is too heavy to churn on each of those.
// Every prop QueryEditorTab passes must stay referentially stable — see the
// stabilized callbacks / EMPTY constants there before adding a new prop.
export const QueryToolbar = memo(function QueryToolbar({
  tabId,
  onExecute,
  onExecuteSelection,
  selectedText = '',
  onExecuteWithActualPlan,
  executeScope,
  viewMode = 'sql',
  onViewModeChange,
  onStop,
  onFormat,
  onExplainPlan,
  onOpenFile,
  onSaveFile,
  onSaveFileAs,
  isExecuting = false,
  isCancelling = false,
  isExplainingPlan = false,
  isOpeningFile = false,
  isSavingFile = false,
  disabled = false,
  isDisconnected = false,
  editorOptions,
  onEditorOptionsChange,
  connectionId,
  database,
  onSelectRecentQuery,
  transactionMode,
  transactionState,
  transactionIsolationLevel,
  onTransactionModeChange,
  onTransactionIsolationLevelChange,
  onTransactionCommit,
  onTransactionRollback,
  transactionCommitEnabled = false,
  transactionRollbackEnabled = false,
  transactionModeChangeDisabled = false,
  transactionIsolationLevelState,
  isAiChatOpen = false,
  onToggleAiChat,
  isAnonymousPlsqlBlock: isPlsql = false,
  onDebug,
  onOptimizeQuery,
  aiCliAvailable,
  aiCliDetectionLoading,
}: QueryToolbarProps) {
  const executeShortcutLabel = useShortcutLabel('query.execute');
  const searchHistoryShortcutLabel = useShortcutLabel('global.searchQueryHistory');
  const [isPlanMenuOpen, setIsPlanMenuOpen] = useState(false);
  const [isResultMenuOpen, setIsResultMenuOpen] = useState(false);
  const [isFileMenuOpen, setIsFileMenuOpen] = useState(false);
  const [isOptionsMenuOpen, setIsOptionsMenuOpen] = useState(false);

  // Global editor settings (used as fallback and updated when options change)
  const globalEditorSettings = useAppStore((state) => state.editorSettings);
  const setGlobalEditorSettings = useAppStore((state) => state.setEditorSettings);

  // Use tab's editor options if available, otherwise fall back to global defaults
  const wordWrap = editorOptions?.wordWrap ?? globalEditorSettings.wordWrap;
  const formatOnExecute = editorOptions?.formatOnExecute ?? globalEditorSettings.formatOnExecute;
  // Inline Execute / Peek bar setting is global-only (not per-tab) — QueryEditorTab
  // reads the same store key, so toggling here affects every open query tab.
  const showInlineExecuteBar = globalEditorSettings.showInlineExecuteBar;
  // Result display mode defaults to 'grid' (not persisted globally)
  const resultDisplayMode = editorOptions?.resultDisplayMode ?? 'grid';

  // Handle option changes: update both tab settings and global defaults
  const handleOptionChange = (options: Partial<TabEditorOptions>) => {
    // Update the current tab's settings
    onEditorOptionsChange?.(options);
    // Also update global defaults for future tabs
    setGlobalEditorSettings(options as Partial<EditorSettings>);
  };

  const handleToggleInlineExecuteBar = () => {
    setGlobalEditorSettings({ showInlineExecuteBar: !showInlineExecuteBar });
  };

  // "Selection mode" — when the user has selected a complete, runnable
  // statement, the toolbar Execute button switches its label to "Selection"
  // and runs only the selected SQL on click. This is the legacy
  // DBeaver/DataGrip-style affordance and is INDEPENDENT of the inline
  // Execute / Peek bar setting: the floating bar is an extra surface next to
  // the selection, not a replacement for the toolbar's selection awareness,
  // so the toolbar behaves identically in both modes.
  const selectionTrim = selectedText.trim();
  const selectionRunnable = useMemo(() => {
    if (!onExecuteSelection || executeScope === 'statement') return false;
    if (!selectionTrim) return false;
    return isExecutableStatement(selectionTrim);
  }, [onExecuteSelection, selectionTrim, executeScope]);

  const handleExecuteClick = () => {
    if (selectionRunnable && onExecuteSelection) {
      onExecuteSelection(selectedText);
      return;
    }
    onExecute();
  };

  // `displaySelectionRunnable` drives the visible state (icon, outline
  // style, brightness). It lags behind `selectionRunnable` by a settle
  // window so a mouse drag through partially-runnable SQL doesn't flicker
  // the styling, and updates at the animation's invisible midpoint so
  // the solid ⇄ dashed swap snaps while the outline is collapsed.
  const executeButtonRef = useRef<HTMLButtonElement>(null);
  const [settledSelectionRunnable, setDisplaySelectionRunnable] = useState(selectionRunnable);
  // The Visual view's "Statement" scope shows at once — never through the
  // selection morph's settle delay.
  const displaySelectionRunnable = executeScope !== 'statement' && settledSelectionRunnable;
  useEffect(() => {
    if (selectionRunnable === settledSelectionRunnable) return;
    let swapTimer: ReturnType<typeof setTimeout> | undefined;
    const settleTimer = setTimeout(() => {
      const el = executeButtonRef.current;
      if (el) {
        el.classList.remove('animate-execute-mode-pulse');
        void el.offsetWidth;
        el.classList.add('animate-execute-mode-pulse');
      }
      // Swap visuals at the animation's invisible midpoint (outline
      // width 0), so the user never sees the style snap. Kept at 50% of
      // the pulse duration — update together with the 250ms
      // `execute-mode-pulse` in src/index.css.
      swapTimer = setTimeout(() => {
        setDisplaySelectionRunnable(selectionRunnable);
      }, 125);
      // 80ms settle (motion-token "intent delay") — long enough to filter a
      // mouse drag sweeping through partially-runnable SQL, short enough that
      // the button still reads as reacting to the selection rather than
      // lagging behind it.
    }, 80);
    return () => {
      clearTimeout(settleTimer);
      if (swapTimer) clearTimeout(swapTimer);
    };
  }, [selectionRunnable, settledSelectionRunnable]);

  // Handle result display mode change (tab-only, not global)
  const handleResultDisplayModeChange = (mode: 'grid' | 'plaintext') => {
    onEditorOptionsChange?.({ resultDisplayMode: mode });
  };

  const executeButtonTitle = isDisconnected
    ? 'Connection offline - reconnect to execute queries'
    : disabled
      ? 'Connect to a database to execute queries'
      : executeScope === 'statement'
        ? `Run the statement shown in the Visual view${executeShortcutLabel ? ` (${executeShortcutLabel})` : ''}`
        : selectionRunnable
        ? `Execute selection${executeShortcutLabel ? ` (${executeShortcutLabel})` : ''}`
        : `Execute query${executeShortcutLabel ? ` (${executeShortcutLabel})` : ''}`;

  return (
    <ResponsiveToolbar
      // CH-25: this bar is all action controls (Execute/Stop, Plan, Result
      // display, File, Format, Options, transaction controls) — no
      // connection/database name is rendered inline here (those live in
      // `QueryStatusBar`), so blanket `select-none` is safe.
      className="min-h-9 border-b border-border bg-muted/20 items-start select-none"
      data-testid="query-toolbar"
    >
      <ToolbarSection position="left" className="items-start">
        {isExecuting ? (
          <>
            <Button
              size="sm"
              variant="destructive"
              onClick={onStop}
              // Stays ENABLED while cancelling: a polite cancel can sit
              // unhonoured for a long time, and the second press is the user's
              // escalation (force — Oracle destroys the client socket).
              disabled={!onStop}
              title={isCancelling ? 'Stopping… click again to force the stop (Oracle: drops the connection so the query returns at once — the server may still finish what it already started; other engines keep their normal cancel)' : 'Stop execution'}
              data-testid="stop-query-button"
              data-toolbar-item
            >
              {isCancelling ? (
                <RandomDotmSpinner size={16} />
              ) : (
                <Square className="h-4 w-4" />
              )}
              {isCancelling ? 'Stopping...' : 'Stop'}
            </Button>
          </>
        ) : (
          <Button
            ref={executeButtonRef}
            size="sm"
            onClick={handleExecuteClick}
            disabled={disabled || isExecuting || isDisconnected}
            title={executeButtonTitle}
            data-testid="execute-query-button"
            data-execute-mode={displaySelectionRunnable ? 'selection' : 'full'}
            data-execute-scope={executeScope}
            aria-label={executeScope === 'statement' ? 'Run statement' : undefined}
            data-toolbar-item
            // W7.3 fix round 2, updated U10 (direct user feedback: the
            // inverted-neutral fill from round 2 "shouts" — near-black on
            // light / near-white on dark measured 18.09:1 / 17.11:1 against
            // the page — while the gutter/inline Execute buttons' solid
            // `--primary` blue "looks dimmed" (5.55:1 / 6.01:1). U10
            // introduces `--primary-alt`, a middle-ground grey fill shared
            // by all three Execute surfaces (toolbar / gutter / inline) —
            // see src/index.css's --primary-alt comment and
            // task-U10-report.md for the measured writeup). This is NOT
            // `variant="default"` (that pulls in `bg-primary`, which is BLUE
            // — round 1's trap): no CVA variant renders this shape, so
            // `bg-primary-alt`/`text-primary-alt-foreground` are set
            // directly via `className` (no `variant` prop is passed, so CVA
            // still contributes `default`'s base classes — `bg-primary
            // text-primary-foreground shadow-xs hover:bg-primary/90` — and
            // `Button`'s own `cn()` call, which uses `tailwind-merge`,
            // resolves the conflicts, keeping this `className`'s colors over
            // the unused `default` ones). The hover background is explicitly
            // PINNED (`hover:bg-primary-alt/90`) rather than omitted — this
            // wave's recurring cascade trap is a stale `hover:` utility
            // silently surviving a base-color change because a hover-scoped
            // class and an unscoped class don't occupy the same
            // `tailwind-merge` conflict slot, so both would otherwise survive
            // (turning the button blue again on hover); pinning our own
            // hover class into that same scoped slot is what actually
            // replaces it.
            //
            // This reverses 2909cbfb8's PP-03 outline treatment for THIS
            // button specifically. Plan/Result/Recent stay ghost and
            // File/Format/Options stay ghost icon-only (2909cbfb8's
            // step-down for those siblings is untouched), so the bar still
            // reads as one hierarchy, just with a stronger top: Execute
            // (solid --primary-alt fill, most weight) > Plan/Result/
            // Recent (ghost, mid weight, text labels when uncollapsed) >
            // File/Format/Options (ghost, icon-only, least weight).
            //
            // The CSS `outline` (not a `border`, and unaffected by the
            // fill/icon changes above) is always visible on top of that
            // (subtle when running the whole editor, brighter + dashed when
            // armed for the current selection) and stays BLUE (`--primary`)
            // — the fill moved off blue, the mode-indicator outline didn't.
            // The visible mode is `displaySelectionRunnable`, which lags
            // behind `selectionRunnable` so the styling settles after a drag
            // (rather than flickering through every partial selection). On
            // flip the outline pulses inward to invisible and back out —
            // composed with a subtle whole-button scale breathe — via
            // `animate-execute-mode-pulse`; the fill/outline style swap
            // happens at the collapsed midpoint, and the icon + label
            // cross-morph (see below) starts there too. Click handler still
            // uses the immediate value so Execute always acts on the
            // current selection.
            className={`bg-primary-alt text-primary-alt-foreground hover:bg-primary-alt/90 ${
              displaySelectionRunnable
                ? 'outline outline-1 outline-dashed outline-offset-[2px] outline-primary/70 brightness-110 shadow-md'
                : 'outline outline-1 outline-solid outline-offset-[2px] outline-primary/30'
            }`}
          >
            {/* Icon and label each render BOTH variants stacked in one grid
                cell (`.exec-morph-*` in src/index.css): the slot is always as
                wide as its widest variant, so the button keeps one fixed size
                across the full ⇄ selection flip and the toolbar never shifts.
                The inactive variant is faded/blurred out by CSS keyed off the
                button's `data-execute-mode`, giving a subtle cross-morph
                (Play ⇄ TextSelect, "Execute" ⇄ "Selection") that starts at
                the pulse's collapsed midpoint when `displaySelectionRunnable`
                flips.
                The orange "work in flight" glyph is fill-aware, not
                theme-aware (U10): `--primary-alt` is a dark-ish grey in
                light theme and a light-ish grey in dark theme, so BOTH arms
                need the fixed, theme-invariant orange tuned for their fill's
                lightness rather than either theme's own plain --energy.
                `text-energy-on-dark-fill` (light theme, default) is the
                brighter orange — light theme's own --energy only measures
                2.71:1 against this grey and fails 3:1.
                `dark:text-energy-on-light-fill` (dark theme) is the darker
                orange — dark theme's own --energy measures 1.68:1 against
                this grey and fails much worse. See the
                --energy-on-light-fill / --energy-on-dark-fill token
                comments in src/index.css and task-U10-report.md for the
                full measured writeup. */}
            <span className="inline-grid place-items-center" aria-hidden="true">
              <Play className="h-4 w-4 exec-morph-icon exec-morph-full text-energy-on-dark-fill dark:text-energy-on-light-fill" />
              <TextSelect className="h-4 w-4 exec-morph-icon exec-morph-selection text-energy-on-dark-fill dark:text-energy-on-light-fill" />
            </span>
            <span className="inline-grid">
              <span
                className="exec-morph-label exec-morph-full text-center"
                data-testid="execute-label-full"
                aria-hidden={displaySelectionRunnable}
              >
                {executeScope === 'statement' ? 'Statement' : 'Execute'}
              </span>
              <span
                className="exec-morph-label exec-morph-selection text-center"
                data-testid="execute-label-selection"
                aria-hidden={!displaySelectionRunnable}
              >
                Selection
              </span>
            </span>
          </Button>
        )}

        {onDebug && isPlsql && (
          <Button
            size="sm"
            variant="outline"
            onClick={onDebug}
            disabled={disabled || isExecuting || isDisconnected}
            title="Debug this anonymous PL/SQL block"
            data-testid="debug-plsql-button"
            data-toolbar-item
          >
            <Bug className="h-4 w-4" />
            Debug
          </Button>
        )}

        {/* Transaction controls */}
        {transactionMode && onTransactionModeChange && onTransactionCommit && onTransactionRollback && onTransactionIsolationLevelChange && transactionIsolationLevelState && (
          <TransactionToolbarControls
            transactionMode={transactionMode}
            transactionState={transactionState ?? 'idle'}
            isolationLevel={transactionIsolationLevel ?? 'default'}
            onModeChange={onTransactionModeChange}
            onIsolationLevelChange={onTransactionIsolationLevelChange}
            onCommit={onTransactionCommit}
            onRollback={onTransactionRollback}
            commitEnabled={transactionCommitEnabled}
            rollbackEnabled={transactionRollbackEnabled}
            modeChangeDisabled={transactionModeChangeDisabled}
            isolationLevelState={transactionIsolationLevelState}
            disabled={disabled || isDisconnected}
          />
        )}

        {/* One divider between the execution group (Execute/Stop, Debug,
            transaction controls) and the document group (Plan/Result/Recent
            and the file/format/options actions) — PP-03 hierarchy. Matches
            the divider TransactionToolbarControls already draws between its
            own clusters (`h-8 w-px bg-border`), so the toolbar reads as one
            visual language rather than two. */}
        <div
          className="h-8 w-px bg-border"
          data-toolbar-item
          data-testid="toolbar-hierarchy-separator"
        />

        <ToolbarRightActions
          placement="secondary-row"
          transactionMode={transactionMode}
          isFileMenuOpen={isFileMenuOpen}
          setIsFileMenuOpen={setIsFileMenuOpen}
          onOpenFile={onOpenFile}
          onSaveFile={onSaveFile}
          onSaveFileAs={onSaveFileAs}
          isOpeningFile={isOpeningFile}
          isSavingFile={isSavingFile}
          onFormat={onFormat}
          isExecuting={isExecuting}
          isOptionsMenuOpen={isOptionsMenuOpen}
          setIsOptionsMenuOpen={setIsOptionsMenuOpen}
          wordWrap={wordWrap}
          formatOnExecute={formatOnExecute}
          showInlineExecuteBar={showInlineExecuteBar}
          onToggleInlineExecuteBar={handleToggleInlineExecuteBar}
          onOptionChange={handleOptionChange}
          onOptimizeQuery={onOptimizeQuery}
          aiCliAvailable={aiCliAvailable}
          aiCliDetectionLoading={aiCliDetectionLoading}
          viewMode={viewMode}
          onViewModeChange={onViewModeChange}
        />

        <div className="flex items-center gap-2" data-toolbar-left-collapsible-group>
          {(onExplainPlan || onExecuteWithActualPlan) && (
            <PlanDropdown
              isPlanMenuOpen={isPlanMenuOpen}
              setIsPlanMenuOpen={setIsPlanMenuOpen}
              onExplainPlan={onExplainPlan}
              onExecuteWithActualPlan={onExecuteWithActualPlan}
              disabled={disabled}
              isExecuting={isExecuting}
              isDisconnected={isDisconnected}
              isExplainingPlan={isExplainingPlan}
            />
          )}

          <ResultDisplayDropdown
            isResultMenuOpen={isResultMenuOpen}
            setIsResultMenuOpen={setIsResultMenuOpen}
            resultDisplayMode={resultDisplayMode}
            onResultDisplayModeChange={handleResultDisplayModeChange}
          />

          {onSelectRecentQuery && (
            <RecentQueriesDropdown
              connectionId={connectionId}
              database={database}
              onSelectQuery={onSelectRecentQuery}
              disabled={disabled || isDisconnected}
            />
          )}

          <Button
            size="sm"
            variant="ghost"
            title={`Search Query History${searchHistoryShortcutLabel ? ` (${searchHistoryShortcutLabel})` : ''}`}
            data-testid="search-history-button"
            data-toolbar-item
            data-toolbar-left-collapsible
            className="px-2"
            onClick={() => useCommandPaletteStore.getState().openPage('query-history')}
          >
            <Search className="h-4 w-4" />
          </Button>
        </div>
      </ToolbarSection>

      <QueryToolbarRightSection transactionMode={transactionMode}>
        <ToolbarRightActions
          placement="right-section"
          transactionMode={transactionMode}
          isFileMenuOpen={isFileMenuOpen}
          setIsFileMenuOpen={setIsFileMenuOpen}
          onOpenFile={onOpenFile}
          onSaveFile={onSaveFile}
          onSaveFileAs={onSaveFileAs}
          isOpeningFile={isOpeningFile}
          isSavingFile={isSavingFile}
          onFormat={onFormat}
          isExecuting={isExecuting}
          isOptionsMenuOpen={isOptionsMenuOpen}
          setIsOptionsMenuOpen={setIsOptionsMenuOpen}
          wordWrap={wordWrap}
          formatOnExecute={formatOnExecute}
          showInlineExecuteBar={showInlineExecuteBar}
          onToggleInlineExecuteBar={handleToggleInlineExecuteBar}
          onOptionChange={handleOptionChange}
          onOptimizeQuery={onOptimizeQuery}
          aiCliAvailable={aiCliAvailable}
          aiCliDetectionLoading={aiCliDetectionLoading}
          viewMode={viewMode}
          onViewModeChange={onViewModeChange}
        />

        {/* Pin this workspace (Task 20). A query tab has no surface-specific
            ineligibility — the generic no-connection / no-database disabling is
            handled inside WorkspacePinButton's hook. File-backed `.sql` tabs pin
            as a file reference; scratch tabs snapshot the live editor text (F5).
            Analytics + create/unpin/update logic live in WorkspacePinButton. */}
        <WorkspacePinButton tabId={tabId} />

        {onToggleAiChat && (
          <Button
            variant={isAiChatOpen ? 'default' : 'ghost'}
            size="sm"
            className="h-7 px-2"
            onClick={onToggleAiChat}
            title="AI Chat"
            data-testid="ai-chat-button"
          >
            <BotMessageSquare className="h-4 w-4" />
          </Button>
        )}
      </QueryToolbarRightSection>
    </ResponsiveToolbar>
  );
});

// Extracted components to keep file clean and support responsive behavior

function QueryToolbarRightSection({
  transactionMode,
  children,
}: {
  transactionMode?: TransactionMode;
  children: React.ReactNode;
}) {
  const { rightActionsPlacement, rightSectionWrapped } = useResponsiveToolbar();
  const hasTwoTransactionRows = transactionMode === 'manual' || transactionMode === 'smart';
  const shouldLiftToSecondRow =
    hasTwoTransactionRows &&
    rightActionsPlacement === 'primary' &&
    rightSectionWrapped;

  // `self-start` exists ONLY for the two-row transaction layouts, where the
  // left section is two rows tall and the right actions must stay level with
  // its FIRST row. Applying it unconditionally top-aligned the right section
  // against the taller left one (Execute is 32px, these icon buttons are 28px),
  // so the right-hand buttons sat ~4px above centre in the ordinary one-row
  // toolbar. Without it they inherit the toolbar's own `items-center`.
  return (
    <ToolbarSection
      position="right"
      className={
        shouldLiftToSecondRow
          ? 'self-start -mt-10'
          : hasTwoTransactionRows
            ? 'self-start'
            : undefined
      }
    >
      {children}
    </ToolbarSection>
  );
}

interface ToolbarRightActionsProps {
  placement: 'secondary-row' | 'right-section';
  transactionMode?: TransactionMode;
  isFileMenuOpen: boolean;
  setIsFileMenuOpen: (open: boolean) => void;
  onOpenFile?: () => void;
  onSaveFile?: () => void;
  onSaveFileAs?: () => void;
  isOpeningFile?: boolean;
  isSavingFile?: boolean;
  onFormat?: () => void;
  isExecuting?: boolean;
  isOptionsMenuOpen: boolean;
  setIsOptionsMenuOpen: (open: boolean) => void;
  wordWrap: 'on' | 'off';
  formatOnExecute: boolean;
  showInlineExecuteBar: boolean;
  onToggleInlineExecuteBar: () => void;
  onOptionChange: (options: Partial<TabEditorOptions>) => void;
  onOptimizeQuery?: () => void;
  aiCliAvailable?: boolean;
  aiCliDetectionLoading?: boolean;
  viewMode: QueryViewMode;
  onViewModeChange?: (mode: QueryViewMode) => void;
}

function ToolbarRightActions({
  placement,
  transactionMode,
  isFileMenuOpen,
  setIsFileMenuOpen,
  onOpenFile,
  onSaveFile,
  onSaveFileAs,
  isOpeningFile,
  isSavingFile,
  onFormat,
  isExecuting,
  isOptionsMenuOpen,
  setIsOptionsMenuOpen,
  wordWrap,
  formatOnExecute,
  showInlineExecuteBar,
  onToggleInlineExecuteBar,
  onOptionChange,
  onOptimizeQuery,
  aiCliAvailable,
  aiCliDetectionLoading,
  viewMode,
  onViewModeChange,
}: ToolbarRightActionsProps) {
  const { rightActionsPlacement } = useResponsiveToolbar();
  const hasTwoTransactionRows = transactionMode === 'manual' || transactionMode === 'smart';
  const moveIntoSecondRow = hasTwoTransactionRows && rightActionsPlacement === 'secondary';

  if (placement === 'secondary-row' && !moveIntoSecondRow) {
    return null;
  }
  if (placement === 'right-section' && moveIntoSecondRow) {
    return null;
  }

  const actions = (
    <>
      {(onOpenFile || onSaveFile || onSaveFileAs) && (
        <FileDropdown
          isFileMenuOpen={isFileMenuOpen}
          setIsFileMenuOpen={setIsFileMenuOpen}
          onOpenFile={onOpenFile}
          onSaveFile={onSaveFile}
          onSaveFileAs={onSaveFileAs}
          isOpeningFile={isOpeningFile}
          isSavingFile={isSavingFile}
        />
      )}

      {/* SQL | Visual view of this tab — beside Format / Optimize, the other
          actions that work on this tab's SQL. */}
      {onViewModeChange && <QueryViewToggle value={viewMode} onChange={onViewModeChange} />}

      {onFormat && (
        <FormatButton
          onFormat={onFormat}
          isExecuting={isExecuting}
        />
      )}

      {/* R2 (closes G3-F6): "AI: Optimize this query" — moved off the Options
          popover onto the toolbar, right between Format and Options (see
          task-R2-brief.md). Tier 1 collapse, same as FormatButton. Hidden
          (not disabled) when there's no connection yet — mirrors onFormat
          above. */}
      {onOptimizeQuery && (
        <OptimizeQueryButton
          onClick={onOptimizeQuery}
          aiCliAvailable={aiCliAvailable}
          aiCliDetectionLoading={aiCliDetectionLoading}
        />
      )}

      <OptionsDropdown
        isOptionsMenuOpen={isOptionsMenuOpen}
        setIsOptionsMenuOpen={setIsOptionsMenuOpen}
        wordWrap={wordWrap}
        formatOnExecute={formatOnExecute}
        showInlineExecuteBar={showInlineExecuteBar}
        onToggleInlineExecuteBar={onToggleInlineExecuteBar}
        onOptionChange={onOptionChange}
      />
    </>
  );

  if (placement === 'secondary-row') {
    return (
      <div className="flex-1 min-w-0 flex flex-col">
        <div className="h-8" />
        <div className="ml-auto flex items-center gap-2" data-toolbar-secondary-row-group>
          {actions}
        </div>
      </div>
    );
  }

  return <>{actions}</>;
}

interface PlanDropdownProps {
  isPlanMenuOpen: boolean;
  setIsPlanMenuOpen: (open: boolean) => void;
  onExplainPlan?: () => void;
  onExecuteWithActualPlan?: () => void;
  disabled: boolean;
  isExecuting: boolean;
  isDisconnected: boolean;
  isExplainingPlan: boolean;
}

function PlanDropdown({
  isPlanMenuOpen,
  setIsPlanMenuOpen,
  onExplainPlan,
  onExecuteWithActualPlan,
  disabled,
  isExecuting,
  isDisconnected,
  isExplainingPlan,
}: PlanDropdownProps) {
  const { leftCollapseState } = useResponsiveToolbar();
  const isCollapsed = leftCollapseState === 'collapsed';

  return (
    <Popover open={isPlanMenuOpen} onOpenChange={setIsPlanMenuOpen}>
      <PopoverTrigger asChild>
        <Button
          size="sm"
          variant="ghost"
          disabled={disabled || isExecuting || isDisconnected || isExplainingPlan}
          title={isDisconnected ? 'Connection offline - reconnect to capture plans' : 'Execution plan options'}
          data-testid="plan-menu-button"
          data-toolbar-item
          data-toolbar-left-collapsible
          className="px-2"
        >
          {isExplainingPlan ? <RandomDotmSpinner size={16} /> : <FileSearch className="h-4 w-4" />}
          {!isCollapsed && 'Plan'}
          <ChevronDown className="h-4 w-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 p-1">
        {onExplainPlan && (
          <button
            type="button"
            className="w-full flex items-start gap-2 rounded-sm px-2 py-2 text-left hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
            onClick={() => {
              setIsPlanMenuOpen(false);
              onExplainPlan();
            }}
            disabled={isExplainingPlan}
            data-testid="plan-action-explain"
          >
            <FileSearch className="h-4 w-4 mt-0.5 text-muted-foreground" />
            <div className="flex flex-col">
              <div className="text-sm font-medium leading-5">Explain plan</div>
              <div className="text-xs text-muted-foreground leading-4">Capture the estimated execution plan</div>
            </div>
          </button>
        )}

        {onExecuteWithActualPlan && (
          <button
            type="button"
            className="w-full flex items-start gap-2 rounded-sm px-2 py-2 text-left hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
            onClick={() => {
              setIsPlanMenuOpen(false);
              onExecuteWithActualPlan();
            }}
            disabled={isExplainingPlan}
            data-testid="plan-action-execute-actual"
          >
            <Play className="h-4 w-4 mt-0.5 text-muted-foreground" />
            <div className="flex flex-col">
              <div className="text-sm font-medium leading-5">Execute with actual plan</div>
              <div className="text-xs text-muted-foreground leading-4">Run the query and capture the actual plan</div>
            </div>
          </button>
        )}
      </PopoverContent>
    </Popover>
  );
}

interface ResultDisplayDropdownProps {
  isResultMenuOpen: boolean;
  setIsResultMenuOpen: (open: boolean) => void;
  resultDisplayMode: 'grid' | 'plaintext';
  onResultDisplayModeChange: (mode: 'grid' | 'plaintext') => void;
}

function ResultDisplayDropdown({
  isResultMenuOpen,
  setIsResultMenuOpen,
  resultDisplayMode,
  onResultDisplayModeChange,
}: ResultDisplayDropdownProps) {
  const { leftCollapseState } = useResponsiveToolbar();
  const isCollapsed = leftCollapseState === 'collapsed';

  return (
    <Popover open={isResultMenuOpen} onOpenChange={setIsResultMenuOpen}>
      <PopoverTrigger asChild>
        <Button
          size="sm"
          variant="ghost"
          title="Result display mode"
          data-testid="result-menu-button"
          data-toolbar-item
          data-toolbar-left-collapsible
          className="px-2"
        >
          {resultDisplayMode === 'grid' ? (
            <LayoutGrid className="h-4 w-4" />
          ) : (
            <AlignJustify className="h-4 w-4" />
          )}
          {!isCollapsed && 'Result'}
          <ChevronDown className="h-4 w-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 p-1">
        <button
          type="button"
          className="w-full flex items-start gap-2 rounded-sm px-2 py-2 text-left hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          onClick={() => {
            setIsResultMenuOpen(false);
            onResultDisplayModeChange('grid');
          }}
          data-testid="result-mode-grid"
        >
          <LayoutGrid className="h-4 w-4 mt-0.5 text-muted-foreground" />
          <div className="flex flex-col flex-1">
            <div className="text-sm font-medium leading-5">Grid</div>
            <div className="text-xs text-muted-foreground leading-4">Display results in a tabular grid</div>
          </div>
          {resultDisplayMode === 'grid' && <Check className="h-4 w-4 mt-0.5 text-primary" />}
        </button>

        <button
          type="button"
          className="w-full flex items-start gap-2 rounded-sm px-2 py-2 text-left hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          onClick={() => {
            setIsResultMenuOpen(false);
            onResultDisplayModeChange('plaintext');
          }}
          data-testid="result-mode-plaintext"
        >
          <AlignJustify className="h-4 w-4 mt-0.5 text-muted-foreground" />
          <div className="flex flex-col flex-1">
            <div className="text-sm font-medium leading-5">Plain text</div>
            <div className="text-xs text-muted-foreground leading-4">Display results as ASCII text table</div>
          </div>
          {resultDisplayMode === 'plaintext' && <Check className="h-4 w-4 mt-0.5 text-primary" />}
        </button>
      </PopoverContent>
    </Popover>
  );
}

interface FileDropdownProps {
  isFileMenuOpen: boolean;
  setIsFileMenuOpen: (open: boolean) => void;
  onOpenFile?: () => void;
  onSaveFile?: () => void;
  onSaveFileAs?: () => void;
  isOpeningFile?: boolean;
  isSavingFile?: boolean;
}

function FileDropdown({
  isFileMenuOpen,
  setIsFileMenuOpen,
  onOpenFile,
  onSaveFile,
  onSaveFileAs,
  isOpeningFile,
  isSavingFile,
}: FileDropdownProps) {
  return (
    <Popover open={isFileMenuOpen} onOpenChange={setIsFileMenuOpen}>
      <PopoverTrigger asChild>
        {/* Ghost icon button (PP-03 document group) — no permanent text
            label; the chevron still signals "this opens a menu". */}
        <Button
          size="sm"
          variant="ghost"
          title="File actions"
          data-testid="file-menu-button"
          data-toolbar-item
          className="px-2"
        >
          <FileText className="h-4 w-4" />
          <ChevronDown className="h-4 w-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-64 p-1">
        {onOpenFile && (
          <button
            type="button"
            className="w-full flex items-start gap-2 rounded-sm px-2 py-2 text-left hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
            onClick={() => {
              setIsFileMenuOpen(false);
              onOpenFile();
            }}
            disabled={isOpeningFile || isSavingFile}
            data-testid="file-action-open"
          >
            <FolderOpen className="h-4 w-4 mt-0.5 text-muted-foreground" />
            <div className="flex flex-col">
              <div className="text-sm font-medium leading-5">Open…</div>
              <div className="text-xs text-muted-foreground leading-4">Open a .sql file in this tab</div>
            </div>
          </button>
        )}

        {onSaveFile && (
          <button
            type="button"
            className="w-full flex items-start gap-2 rounded-sm px-2 py-2 text-left hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
            onClick={() => {
              setIsFileMenuOpen(false);
              onSaveFile();
            }}
            disabled={isOpeningFile || isSavingFile}
            data-testid="file-action-save"
          >
            <Save className="h-4 w-4 mt-0.5 text-muted-foreground" />
            <div className="flex flex-col">
              <div className="text-sm font-medium leading-5">Save</div>
              <div className="text-xs text-muted-foreground leading-4">Save to the linked file (or pick one)</div>
            </div>
          </button>
        )}

        {onSaveFileAs && (
          <button
            type="button"
            className="w-full flex items-start gap-2 rounded-sm px-2 py-2 text-left hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
            onClick={() => {
              setIsFileMenuOpen(false);
              onSaveFileAs();
            }}
            disabled={isOpeningFile || isSavingFile}
            data-testid="file-action-save-as"
          >
            <SaveAll className="h-4 w-4 mt-0.5 text-muted-foreground" />
            <div className="flex flex-col">
              <div className="text-sm font-medium leading-5">Save As…</div>
              <div className="text-xs text-muted-foreground leading-4">Save to a different file</div>
            </div>
          </button>
        )}
      </PopoverContent>
    </Popover>
  );
}

interface FormatButtonProps {
  onFormat: () => void;
  isExecuting?: boolean;
}

function FormatButton({ onFormat, isExecuting }: FormatButtonProps) {
  const shortcutLabel = useShortcutLabel('query.formatSql');
  return (
    // Ghost icon button (PP-03 document group) — no permanent text label.
    <Button
      size="sm"
      variant="ghost"
      onClick={onFormat}
      disabled={isExecuting}
      title={`Format SQL${shortcutLabel ? ` (${shortcutLabel})` : ''}`}
      data-testid="format-sql-button"
      data-toolbar-item
      className="px-2"
    >
      <Wand2 className="h-4 w-4" />
    </Button>
  );
}

interface OptionsDropdownProps {
  isOptionsMenuOpen: boolean;
  setIsOptionsMenuOpen: (open: boolean) => void;
  wordWrap: 'on' | 'off';
  formatOnExecute: boolean;
  showInlineExecuteBar: boolean;
  onToggleInlineExecuteBar: () => void;
  onOptionChange: (options: Partial<TabEditorOptions>) => void;
}

function OptionsDropdown({
  isOptionsMenuOpen,
  setIsOptionsMenuOpen,
  wordWrap,
  formatOnExecute,
  showInlineExecuteBar,
  onToggleInlineExecuteBar,
  onOptionChange,
}: OptionsDropdownProps) {
  return (
    <Popover open={isOptionsMenuOpen} onOpenChange={setIsOptionsMenuOpen}>
      <PopoverTrigger asChild>
        {/* Ghost icon button (PP-03 document group) — no permanent text
            label; the chevron still signals "this opens a menu". */}
        <Button
          size="sm"
          variant="ghost"
          title="Editor options"
          data-testid="options-menu-button"
          data-toolbar-item
          className="px-2"
        >
          <Settings2 className="h-4 w-4" />
          <ChevronDown className="h-4 w-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-64 p-1">
        <button
          type="button"
          className="w-full flex items-start gap-2 rounded-sm px-2 py-2 text-left hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          onClick={() => onOptionChange({ wordWrap: wordWrap === 'on' ? 'off' : 'on' })}
          data-testid="option-word-wrap"
        >
          <WrapText className="h-4 w-4 mt-0.5 text-muted-foreground" />
          <div className="flex flex-col flex-1">
            <div className="text-sm font-medium leading-5">Word wrap</div>
            <div className="text-xs text-muted-foreground leading-4">Wrap long lines in the editor</div>
          </div>
          {wordWrap === 'on' && <Check className="h-4 w-4 mt-0.5 text-primary" />}
        </button>

        <button
          type="button"
          className="w-full flex items-start gap-2 rounded-sm px-2 py-2 text-left hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          onClick={() => onOptionChange({ formatOnExecute: !formatOnExecute })}
          data-testid="option-format-on-execute"
        >
          <Wand2 className="h-4 w-4 mt-0.5 text-muted-foreground" />
          <div className="flex flex-col flex-1">
            <div className="text-sm font-medium leading-5">Format on execute</div>
            <div className="text-xs text-muted-foreground leading-4">Auto-format SQL before running</div>
          </div>
          {formatOnExecute && <Check className="h-4 w-4 mt-0.5 text-primary" />}
        </button>

        <button
          type="button"
          className="w-full flex items-start gap-2 rounded-sm px-2 py-2 text-left hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          onClick={onToggleInlineExecuteBar}
          data-testid="option-inline-execute-bar"
        >
          <MousePointerClick className="h-4 w-4 mt-0.5 text-muted-foreground" />
          <div className="flex flex-col flex-1">
            <div className="text-sm font-medium leading-5">Inline Execute / Peek bar</div>
            <div className="text-xs text-muted-foreground leading-4">Floating bar next to selections and the cursor's statement (off = gutter buttons instead)</div>
          </div>
          {showInlineExecuteBar && <Check className="h-4 w-4 mt-0.5 text-primary" />}
        </button>
      </PopoverContent>
    </Popover>
  );
}
